import { StageDetails } from "../../types/tournament";
import { IMatchDetailDTO } from "../../models/IMatch";
import { finishAmericano } from "../../api/tournaments";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { americanoFinishSummary } from "../../utils/methods";
import { Alert, ConfirmDialog } from "../../ui";
import "./styles.scss";

type FinishEarlyDialogProps = {
  stage: StageDetails;
  matches: IMatchDetailDTO[];
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// Americano "Finish early": a confirm dialog, like Delete schedule.
const FinishEarlyDialog = (props: FinishEarlyDialogProps) => {
  const dispatch = useAppDispatch();

  const handleFinish = async () => {
    try {
      const { data } = await finishAmericano(props.stage.id);
      dispatch(
        setGeneralAlertMessage({
          type: "success",
          message: `Finished after ${count(data.roundsPlayed, "round", "rounds")} — ${count(
            data.matchesRemoved,
            "unplayed match",
            "unplayed matches"
          )} removed.`,
        })
      );
    } catch (error: any) {
      // The api interceptor rejects with the response body, so the backend's reason is error.message.
      // A thrown Error (e.g. no response at all) is not a backend message - use the fallback.
      dispatch(
        setGeneralAlertMessage({
          type: "error",
          message:
            (!(error instanceof Error) && error?.message) || "Couldn't finish the event early. Please try again.",
        })
      );
      // Re-thrown so the dialog stays open, as before.
      throw error;
    }
  };

  const { roundsPlayed, unplayedMatches, inProgress } = americanoFinishSummary(props.matches ?? []);

  return (
    <ConfirmDialog
      open={props.isOpen}
      onClose={(confirmed) => props.onClose(confirmed || undefined)}
      onConfirm={handleFinish}
      title="Finish early?"
      message={
        <p>
          Finish after {count(roundsPlayed, "round", "rounds")}?{" "}
          {count(unplayedMatches, "unplayed match", "unplayed matches")} will be removed and the leaderboard becomes
          final. This can't be undone.
        </p>
      }
      confirmLabel="Finish early"
    >
      {inProgress.length > 0 && (
        <Alert tone="danger" className="finish-early-dialog__warning">
          {inProgress.join(", ")} {inProgress.length === 1 ? "is" : "are"} still in progress — finish or clear{" "}
          {inProgress.length === 1 ? "its score" : "their scores"} first.
        </Alert>
      )}
    </ConfirmDialog>
  );
};

export default FinishEarlyDialog;
